import { useState, useCallback, useRef } from 'react';

const MAX_CONCURRENT_DOWNLOADS = 5;
const DOWNLOAD_START_DELAY_MS = 100;
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export interface QueueStats {
  activeCount: number;
  queuedCount: number;
  completedCount: number;
  failedCount: number;
  totalCount: number;
}

interface QueueItem<T> {
  id: string;
  data: T;
  status: 'queued' | 'active' | 'completed' | 'failed';
  error?: string;
}

export function useDownloadQueue<T, R>() {
  const [stats, setStats] = useState<QueueStats>({
    activeCount: 0,
    queuedCount: 0,
    completedCount: 0,
    failedCount: 0,
    totalCount: 0,
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const queueRef = useRef<QueueItem<T>[]>([]);
  const activePromises = useRef<Set<Promise<void>>>(new Set());

  const updateStats = useCallback(() => {
    const queue = queueRef.current;
    setStats({
      activeCount: queue.filter(item => item.status === 'active').length,
      queuedCount: queue.filter(item => item.status === 'queued').length,
      completedCount: queue.filter(item => item.status === 'completed').length,
      failedCount: queue.filter(item => item.status === 'failed').length,
      totalCount: queue.length,
    });
  }, []);

  const processUrls = useCallback(async (
    items: T[],
    processFn: (item: T) => Promise<R>,
    getId: (item: T) => string
  ): Promise<Map<string, R | Error>> => {
    setIsProcessing(true);
    
    // Initialize queue
    queueRef.current = items.map(item => ({
      id: getId(item),
      data: item,
      status: 'queued' as const,
    }));
    
    updateStats();

    const results = new Map<string, R | Error>();
    const queue = queueRef.current;

    const processNext = async (): Promise<void> => {
      // Find next queued item
      const nextItem = queue.find(item => item.status === 'queued');
      if (!nextItem) {
        return;
      }

      // Mark as active
      nextItem.status = 'active';
      updateStats();

      // Add delay before starting download
      await sleep(DOWNLOAD_START_DELAY_MS);

      try {
        const result = await processFn(nextItem.data);
        results.set(nextItem.id, result);
        nextItem.status = 'completed';
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error');
        results.set(nextItem.id, err);
        nextItem.status = 'failed';
        nextItem.error = err.message;
      }

      updateStats();
    };

    // Process queue with concurrency limit
    while (
      queue.some(item => item.status === 'queued') ||
      queue.some(item => item.status === 'active')
    ) {
      // Fill up to max concurrent
      while (
        activePromises.current.size < MAX_CONCURRENT_DOWNLOADS &&
        queue.some(item => item.status === 'queued')
      ) {
        const promise = processNext().finally(() => {
          activePromises.current.delete(promise);
        });
        activePromises.current.add(promise);
      }

      // Wait for at least one to complete if we have active promises
      if (activePromises.current.size > 0) {
        await Promise.race(activePromises.current);
      }
    }

    // Wait for all remaining promises
    if (activePromises.current.size > 0) {
      await Promise.all(activePromises.current);
    }

    setIsProcessing(false);
    return results;
  }, [updateStats]);

  const getProgress = useCallback(() => {
    const { completedCount, failedCount, totalCount } = stats;
    if (totalCount === 0) return 0;
    return ((completedCount + failedCount) / totalCount) * 100;
  }, [stats]);

  return {
    processUrls,
    stats,
    isProcessing,
    progress: getProgress(),
  };
}